import { apiSlice } from './apiSlice';
import { Role, Wallet, LoginAsTokenResponse, VerifyLoginTokenResponse } from '@/types';
import { setUser, UserResponse } from '../slices/userSlice';
import { setAvailableRoles } from '../slices/availableRolesSlice';
interface ChangePasswordResponse {
  status: number;
  message: string;
}
export const authApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<
      {
        accessToken: string;
        type: string;
        role: {
          name: string;
          id: number;
        };
        // transactionCode?: string | null;
      },
      {
        username: string;
        password: string;
        // captchaId: string;
        // captchaText: string;
      }
    >({
      query: (body) => ({
        url: '/auth/login',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    // new captcha endpoint
    getCaptcha: builder.query<any, void>({
      query: () => ({
        url: '/captcha',
        method: 'POST',
      }),
    }),

    logout: builder.mutation<any, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
    }),
    getUser: builder.query<UserResponse, void>({
      query: () => ({ url: '/users/me', method: 'GET', params: { ts: Date.now() } }),

      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUser(data));
        } catch (err) {
          console.error(err);
        }
      },
      providesTags: ['User'],
    }),
    availbleRoles: builder.query<Role[], void>({
      query: () => ({
        url: '/role/available',
        method: 'GET',
        params: { ts: Date.now() },
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setAvailableRoles(data));
        } catch (err) {
          console.error(err);
        }
      },
      // providesTags: ['User'],
    }),
    changePassword: builder.mutation<
      { success: boolean; status?: string },
      { oldPassword: string; newPassword: string }
    >({
      query: (body) => ({
        url: '/users/me/change-password',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    OwnerChangePassword: builder.mutation<
      ChangePasswordResponse,
      { oldPassword: string; newPassword: string }
    >({
      query: (body) => ({
        url: '/admin/change-password',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),

    getWallet: builder.query<Wallet[], void>({
      query: () => ({
        url: '/users/me/wallets',
        method: 'GET',
        params: { ts: Date.now() },
      }),
      providesTags: ['Wallet'],
    }),

    // "Login as" - generates a one-time code for logging in as a downline user on the agent panel
    getLoginAsToken: builder.query<LoginAsTokenResponse, { userId: string | number }>({
      query: ({ userId }) => ({
        url: `/auth/gettoken/${userId}`,
        method: 'GET',
        responseHandler: 'text',
      }),
      // backend may respond with a raw code, a JSON-quoted string, or an object - normalize all of them
      transformResponse: (response: string): LoginAsTokenResponse => {
        let code = (response ?? '').trim();
        try {
          const parsed = JSON.parse(code);
          if (typeof parsed === 'string') {
            code = parsed;
          } else if (parsed && typeof parsed === 'object') {
            code = parsed.code ?? parsed.token ?? code;
          }
        } catch {
          // not JSON - use the raw text as-is
        }
        return { code };
      },
    }),
    verifyLoginToken: builder.query<VerifyLoginTokenResponse, { token: string }>({
      query: ({ token }) => ({
        url: `/auth/verify-token/${token}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useGetCaptchaQuery,
  useLazyGetCaptchaQuery,
  useLogoutMutation,
  useGetUserQuery,
  useChangePasswordMutation,
  useAvailbleRolesQuery,
  useGetWalletQuery,
  useOwnerChangePasswordMutation,
  useLazyGetLoginAsTokenQuery,
  useLazyVerifyLoginTokenQuery,
  useGetLoginAsTokenQuery,
} = authApi;
